import { useCallback } from 'react';
import { Map as NewbuildingMapComponent } from '@cian/frontend-newbuilding-map-component';
import { toRegularGeoObjects, toTransportAccessibilityGeoObjects } from '../helpers/mappers';
import { IRegularInfrastructureData, ITAInfrastructureData, TTAInfrastructureType } from '../../../types/map';
import { useTransportAccessibilityZoom } from './useTransportAccessibilityZoom';

interface IUseSetGeoObjectsCallbacksResult {
  /**
   * Устанавливает геообъекты обычной инфраструктуры по типам и году.
   */
  setRegularGeoObjects: (currentTypes: string[], selectedYear: number, isMapServicesVisible: boolean) => void;
  /**
   * Устанавливает геообъекты сервиса транспортной доступности по типу.
   */
  setTransportAccessibilityGeoObjects: (infrastructureType: TTAInfrastructureType | null) => void;
}

/**
 * Возвращает функции установки геообъектов на карту.
 */
export const useSetGeoObjectsCallbacks = (
  mapApi: NewbuildingMapComponent | null,
  regularInfrastructureByYear: Map<number, IRegularInfrastructureData>,
  transportAccessibilityInfrastructureByType: Map<TTAInfrastructureType, ITAInfrastructureData>,
  currentTypes: string[],
  selectedYear: number,
  setFutureTypes: (selectedYear: number | null) => void,
  resetTARouting: () => void,
): IUseSetGeoObjectsCallbacksResult => {
  const { setTransportAccessibilityZoom } = useTransportAccessibilityZoom(mapApi);

  const setRegularGeoObjects = useCallback(
    (currentTypes: string[], selectedYear: number, onlyExisting: boolean) => {
      const geoObjects = toRegularGeoObjects(regularInfrastructureByYear, selectedYear, onlyExisting);
      const geoObjectsByType = geoObjects.filter(item => currentTypes.includes(item.name));

      // TODO: Будет удалено при выпиле фича-флага в NB-1172.
      if (onlyExisting) {
        setFutureTypes(null);
      } else {
        setFutureTypes(selectedYear);
      }

      mapApi?.removeAllGeoObjects();
      mapApi?.addGeoObjects(geoObjectsByType);
    },
    [mapApi, regularInfrastructureByYear, setFutureTypes],
  );

  const setTransportAccessibilityGeoObjects = useCallback(
    async (infrastructureType: TTAInfrastructureType | null) => {
      if (infrastructureType) {
        const { isZoomChanged, animationDelay } = setTransportAccessibilityZoom(infrastructureType);

        if (isZoomChanged) {
          // Ждем окончания анимации изменения зума.
          await new Promise(resolve => setTimeout(resolve, animationDelay));
        }

        const geoObjects = toTransportAccessibilityGeoObjects(
          transportAccessibilityInfrastructureByType,
          infrastructureType,
        );

        mapApi?.removeAllGeoObjects();
        mapApi?.addGeoObjects(geoObjects);
      } else {
        setRegularGeoObjects(currentTypes, selectedYear, true);
      }
      resetTARouting();
    },
    [
      mapApi,
      transportAccessibilityInfrastructureByType,
      currentTypes,
      selectedYear,
      setRegularGeoObjects,
      resetTARouting,
      setTransportAccessibilityZoom,
    ],
  );

  return {
    setRegularGeoObjects,
    setTransportAccessibilityGeoObjects,
  };
};
